/**
 * Shared Free-plan wall: parse API errors and show UpgradePrompt in place.
 */

import React, { useCallback, useMemo, useState } from 'react';
import UpgradePrompt from '../components/UpgradePrompt';
import { getErrorPayload, isPlanLimitPayload } from '../utils/planLimit';

/**
 * Hook that opens the Pro modal when a quota error comes back.
 *
 * @param {{ rewriteCvId?: string|number, rewriteJob?: string }} [options]
 *   When the analysis paywall is shown, the AI-rewrite button reuses this CV.
 * @returns {{
 *   handlePlanLimitError: (error: Error) => Promise<boolean>,
 *   showPlanLimit: (payload: Object) => boolean,
 *   prompt: React.ReactNode,
 *   open: boolean,
 * }}
 */
export function usePlanLimitPrompt({ rewriteCvId = '', rewriteJob = '' } = {}) {
  const [upgrade, setUpgrade] = useState(null);

  const showPlanLimit = useCallback((payload) => {
    if (!isPlanLimitPayload(payload)) {
      return false;
    }
    setUpgrade(payload);
    return true;
  }, []);

  const handlePlanLimitError = useCallback(
    async (error) => {
      const payload = await getErrorPayload(error);
      return showPlanLimit(payload);
    },
    [showPlanLimit]
  );

  const prompt = useMemo(
    () => (
      <UpgradePrompt
        open={Boolean(upgrade)}
        errorCode={upgrade?.error_code || ''}
        rewriteCvId={rewriteCvId}
        rewriteJob={rewriteJob}
        onClose={() => setUpgrade(null)}
      />
    ),
    [upgrade, rewriteCvId, rewriteJob]
  );

  return { handlePlanLimitError, showPlanLimit, prompt, open: Boolean(upgrade) };
}
